import React, { useState, useCallback } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Dialog, Portal, Text } from 'react-native-paper';
import {
  FormControlLabel,
  Switch as MuiSwitch,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import useOrientation from 'shared/hooks/useOrientation';
import useSnackBar from 'shared/hooks/useSnackBar';
import { sizes } from 'shared/styles';
import { COMP_URL, EMBED_ORIGIN } from 'utils/config';
import { LOCALE_OPTIONS, SupportedLocale } from 'shared/i18n';

const STORAGE_KEY = 'leaderboard_settings';

/** Escape a string for use inside double-quoted HTML attributes */
const escapeHtmlAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

interface LeaderboardSettings {
  primaryColor: string;
  oddColor: string;
  evenColor: string;
  bgColor: string;
  dark: boolean;
  local: boolean;
  hero: boolean;
  colorCustomizationEnabled: boolean;
  locale: string;
}

const defaultSettings: LeaderboardSettings = {
  primaryColor: '',
  oddColor: '',
  evenColor: '',
  bgColor: '',
  dark: false,
  local: false,
  hero: false,
  colorCustomizationEnabled: false,
  locale: '',
};

const getSettings = (): LeaderboardSettings => {
  if (typeof window === 'undefined') {
    return defaultSettings;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Error reading leaderboard settings from localStorage:', e);
  }
  return defaultSettings;
};

function useLeaderboardSettings() {
  // Use a counter to force re-renders when settings change
  const [, forceUpdate] = useState(0);

  // Read directly from localStorage on each render (reactive)
  const settings = getSettings();

  const updateSettings = useCallback(
    (updates: Partial<LeaderboardSettings>) => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        const current = getSettings();
        const updated = { ...current, ...updates };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        forceUpdate((n) => n + 1);
      } catch (e) {
        console.error('Error writing leaderboard settings to localStorage:', e);
      }
    },
    []
  );

  return {
    ...settings,
    setPrimaryColor: (value: string) => updateSettings({ primaryColor: value }),
    setOddColor: (value: string) => updateSettings({ oddColor: value }),
    setEvenColor: (value: string) => updateSettings({ evenColor: value }),
    setBgColor: (value: string) => updateSettings({ bgColor: value }),
    setDark: (value: boolean) => updateSettings({ dark: value }),
    setLocal: (value: boolean) => updateSettings({ local: value }),
    setHero: (value: boolean) => updateSettings({ hero: value }),
    setColorCustomizationEnabled: (value: boolean) =>
      updateSettings({ colorCustomizationEnabled: value }),
    setLocale: (value: string) => updateSettings({ locale: value }),
    updateSettings,
  };
}

interface Props {
  visible: boolean;
  trackIdToken: string;
  isLocalComp: boolean;
  onDismiss: () => void;
}

const LeaderboardCustomizationModal = ({
  visible,
  trackIdToken,
  isLocalComp,
  onDismiss,
}: Props) => {
  const { vh } = useOrientation();
  const showSnackBar = useSnackBar();

  const {
    primaryColor,
    oddColor,
    evenColor,
    bgColor,
    dark,
    local,
    hero,
    colorCustomizationEnabled,
    locale,
    setPrimaryColor,
    setOddColor,
    setEvenColor,
    setBgColor,
    setDark,
    setLocal,
    setHero,
    setColorCustomizationEnabled,
    setLocale,
  } = useLeaderboardSettings();

  const buildParams = () => {
    const params = new URLSearchParams();

    // Remove # prefix from colors for URL parameters
    const removeHash = (color: string) => color.replace(/^#/, '');

    if (colorCustomizationEnabled) {
      if (primaryColor) {
        params.append('primary_color', removeHash(primaryColor));
      }
      if (oddColor) {
        params.append('odd_color', removeHash(oddColor));
      }
      if (evenColor) {
        params.append('even_color', removeHash(evenColor));
      }
      if (bgColor) {
        params.append('bg_color', removeHash(bgColor));
      }
    }
    if (dark) {
      params.append('dark', 'true');
    }
    if (local && isLocalComp) {
      params.append('local', 'true');
    }
    if (hero) {
      params.append('hero', 'true');
    }
    if (locale) {
      params.append('locale', locale);
    }

    return params.toString();
  };

  const buildUrl = () => {
    const params = buildParams();
    const baseUrl = `${COMP_URL}/leaderboard/${trackIdToken}`;
    return params ? `${baseUrl}?${params}` : baseUrl;
  };

  const buildEmbedPath = () => {
    const params = buildParams();
    const basePath = `/leaderboard/${trackIdToken}`;
    return params ? `${basePath}?${params}` : basePath;
  };

  const buildEmbedCode = () => {
    const embedPath = buildEmbedPath();
    const scriptSrc = `${EMBED_ORIGIN}/assets/embed-leaderboard.js`;

    return `<div id="ergzone-leaderboard" data-path="${escapeHtmlAttr(embedPath)}"></div>
<script src="${escapeHtmlAttr(scriptSrc)}"></script>`;
  };

  const copyToClipboard = (text: string, message: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        showSnackBar({ message });
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showSnackBar({ message });
      } catch (err) {
        showSnackBar({ message: 'Failed to copy to clipboard' });
      }
      document.body.removeChild(textArea);
    }
  };

  const handleOpenUrl = () => {
    window.open(buildUrl(), '_blank');
  };

  const handleCopyUrl = () => {
    copyToClipboard(buildUrl(), 'URL copied to clipboard');
  };

  const handleCopyEmbedCode = () => {
    copyToClipboard(buildEmbedCode(), 'Embed code copied to clipboard');
  };

  return (
    <Portal>
      <Dialog
        visible={visible}
        onDismiss={onDismiss}
        style={[{ maxHeight: vh(80) }, styles.dialog]}
      >
        <Dialog.Title>Customize Leaderboard</Dialog.Title>
        <ScrollView contentContainerStyle={styles.scrollView}>
          <View>
            <Text style={styles.sectionTitle}>Options</Text>

            <FormControlLabel
              control={
                <MuiSwitch
                  checked={dark}
                  onChange={(e) => setDark(e.target.checked)}
                />
              }
              label="Dark Mode (white text)"
            />

            {isLocalComp && (
              <FormControlLabel
                control={
                  <MuiSwitch
                    checked={local}
                    onChange={(e) => setLocal(e.target.checked)}
                  />
                }
                label="Show Signup Button"
              />
            )}

            <FormControlLabel
              control={
                <MuiSwitch
                  checked={hero}
                  onChange={(e) => setHero(e.target.checked)}
                />
              }
              label="Show Hero Banner"
            />

            <FormControl style={{ marginTop: 15, minWidth: 200 }}>
              <InputLabel>Language</InputLabel>
              <Select
                style={{ width: 200 }}
                value={locale || SupportedLocale.en}
                onChange={(e) => setLocale(e.target.value as string)}
                displayEmpty
              >
                {LOCALE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </View>
          <View style={styles.colorSection}>
            <Text style={styles.sectionTitle}>Colors</Text>

            <FormControlLabel
              control={
                <MuiSwitch
                  checked={colorCustomizationEnabled}
                  onChange={(e) =>
                    setColorCustomizationEnabled(e.target.checked)
                  }
                />
              }
              label="Enable Color Customization"
            />

            {colorCustomizationEnabled && (
              <>
                <View style={styles.colorInput}>
                  <label style={styles.colorLabel}>
                    <input
                      type="color"
                      value={primaryColor || '#ffffff'}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      style={{ marginRight: 10, width: 50, height: 30 }}
                    />
                    Primary Color (table header & buttons)
                  </label>
                  <TextField
                    size="small"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="Default color"
                    style={{ width: 100, marginLeft: 10 }}
                  />
                </View>

                <View style={styles.colorInput}>
                  <label style={styles.colorLabel}>
                    <input
                      type="color"
                      value={oddColor || '#ffffff'}
                      onChange={(e) => setOddColor(e.target.value)}
                      style={{ marginRight: 10, width: 50, height: 30 }}
                    />
                    Odd Row Color
                  </label>
                  <TextField
                    size="small"
                    value={oddColor}
                    onChange={(e) => setOddColor(e.target.value)}
                    placeholder="Default color"
                    style={{ width: 100, marginLeft: 10 }}
                  />
                </View>

                <View style={styles.colorInput}>
                  <label style={styles.colorLabel}>
                    <input
                      type="color"
                      value={evenColor || '#ffffff'}
                      onChange={(e) => setEvenColor(e.target.value)}
                      style={{ marginRight: 10, width: 50, height: 30 }}
                    />
                    Even Row Color
                  </label>
                  <TextField
                    size="small"
                    value={evenColor}
                    onChange={(e) => setEvenColor(e.target.value)}
                    placeholder="Default color"
                    style={{ width: 100, marginLeft: 10 }}
                  />
                </View>

                <View style={styles.colorInput}>
                  <label style={styles.colorLabel}>
                    <input
                      type="color"
                      value={bgColor || '#ffffff'}
                      onChange={(e) => setBgColor(e.target.value)}
                      style={{ marginRight: 10, width: 50, height: 30 }}
                    />
                    Background Color
                  </label>
                  <TextField
                    size="small"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    placeholder="Default color"
                    style={{ width: 100, marginLeft: 10 }}
                  />
                </View>
              </>
            )}
          </View>
          <View style={styles.embedSection}>
            <Text style={styles.sectionTitle}>URL</Text>
            <pre
              // @ts-ignore
              style={webStyles.codeBlock}
            >
              <code>{buildUrl()}</code>
            </pre>
          </View>
          <View style={styles.embedSection}>
            <Text style={styles.sectionTitle}>Embed Settings</Text>
            <Text style={styles.descriptionText}>
              Please contact our support team at support@erg.zone and provide
              your website domain so it can be configured to work. After it is
              configured, you can use the embed code below to display the
              leaderboard on your own website.
            </Text>

            <View style={styles.embedCodeContainer}>
              <Text style={styles.embedCodeLabel}>Embed Code:</Text>
              <pre
                // @ts-ignore
                style={webStyles.codeBlock}
              >
                <code>{buildEmbedCode()}</code>
              </pre>
            </View>
          </View>
        </ScrollView>

        <Dialog.Actions style={styles.actionsContainer}>
          <Button mode="outlined" onPress={onDismiss}>
            Cancel
          </Button>
          <Button mode="outlined" icon="content-copy" onPress={handleCopyUrl}>
            Copy URL
          </Button>
          <Button
            mode="outlined"
            icon="code-tags"
            onPress={handleCopyEmbedCode}
          >
            Copy Embed
          </Button>
          <Button mode="contained" icon="open-in-new" onPress={handleOpenUrl}>
            Open URL
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: Platform.select({
    default: {},
    web: {
      alignSelf: 'center',
      width: 600,
    },
  }),
  content: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  colorSection: {
    marginTop: 20,
  },
  embedSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
    marginTop: 10,
  },
  colorInput: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  embedCodeContainer: {
    marginTop: 10,
  },
  embedCodeLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 14,
    marginBottom: 15,
    color: '#666',
    lineHeight: 20,
  },
  colorLabel: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    cursor: 'pointer',
  },
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  scrollView: {
    paddingHorizontal: 20,
  },
});

const webStyles = {
  codeBlock: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: 12,
    margin: 0,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre',
    width: '100%',
    boxSizing: 'border-box',
  },
};

export default LeaderboardCustomizationModal;
